<template lang="pug">
    .upload
        //- accept="application/pdf, application/msexcel, vnd.openxmlformats-officedocument.spreadsheetml.sheet, image/jpg, image/jpeg, image/png"
        el-upload.plus-box(
            ref="upload"
            drag multiple :action="uploadEndpoint"
            :accept="accept || undefined"
            :before-upload="validateUpload"
            :http-request="customUploadRequest"
            :on-success="uploadSucceed"
            :on-error="uploadFailed"
            :headers="uploadHeaders"
            :data="uploadData"
        )
            i.el-icon-plus
            // i.el-icon-upload.el-icon-fa-cloud-upload-alt.fas
            .el-upload__text(v-html="$t('upload.drop.plural')")
            .el-upload__tip(slot="tip") {{ $t('upload.validation', { filetypes: uploadTipFiletypes, size: '2GB' }) }}
        el-dialog(
            :title="$t('There is already a file with the same name')"
            :visible.sync="modal.visibility"
            :close-on-click-modal="false"
            :append-to-body="true"
            top="10vh"
            width="740px"
        )
            el-form.file-modal-form(
                v-if="invalidFiles && invalidFiles.length"
                :model="modal"
                label-position="top"
                ref="modal"
                @submit.prevent.native="submitModalForm"
            )
                h3 {{ invalidFiles[0].old.name }}
                .flex
                    .col.old
                        p {{ $t('Do you want to replace the existing file') }}
                        .icon
                            i.el-icon-document-delete
                        .info
                            dl
                                dt {{ $t('Type') }}
                                dd {{ invalidFiles[0].old.mime }}
                                dt {{ $t('Size') }}
                                dd {{ parseInt(invalidFiles[0].old.size) | prettyBytes }}
                                dt {{ $t('Modified') }}
                                dd {{ formatDate(invalidFiles[0].old.updated_at) }}

                    .col.new
                        p {{ $t('with the following file?') }}
                        .icon
                            i.el-icon-document-add
                        .info
                            dl
                                dt {{ $t('Type') }}
                                dd {{ invalidFiles[0].new.type }}
                                dt {{ $t('Size') }}
                                dd {{ invalidFiles[0].new.size | prettyBytes }}
                                dt {{ $t('Modified') }}
                                dd {{ formatDate(invalidFiles[0].new.lastModified / 1000) }}

            template(slot="footer")
                .modal-footer
                    .left
                        .switch(v-if="invalidFiles && invalidFiles.length > 1")
                            el-switch(
                                :active-text="$t('Apply this option to all existing files')"
                                v-model="modal.all"
                            )
                        el-button(
                            v-else
                            size="mini"
                            icon="el-icon-fa-times fas"
                            @click="skipFileUpload"
                        ) {{ $t('Cancel') }}
                    .right
                        el-button(
                            v-if="invalidFiles && invalidFiles.length > 1"
                            size="mini"
                            icon="el-icon-fa-times fas"
                            @click="skipFileUpload"
                        ) {{ $t('Skip') }}
                        el-button(
                            size="mini"
                            type="warning"
                            icon="el-icon-fa-file-excel fas"
                            @click="replaceFile"
                        ) {{ $t('Overwrite') }}
                        el-button(
                            size="mini"
                            type="primary"
                            icon="el-icon-fa-clone fas"
                            @click="renameFile"
                        ) {{ $t('Generate a unique name') }}
</template>

<i18n>
{
    "de": {
        "There is already a file with the same name": "Es gibt bereits eine Datei mit demselben Namen",
        "Do you want to replace the existing file": "Soll die existierende Datei",
        "with the following file?": "durch diese ersetzt werden?",
        "Type": "Art",
        "Size": "Grüße",
        "Modified": "Zuletzt verändert am/um",
        "Apply this option to all existing files": "Auf alle Dateien anwenden",
        "Skip": "Verwerfen",
        "Overwrite": "Überschreiben",
        "Generate a unique name": "Eindeutigen Namen generieren",
        "All files were processed successfully": "Alle Dateien wurden erfolgreich verarbeitet",
        "all": "alle"
    }
}
</i18n>

<script>
import _ from 'lodash'
import uniqid from 'uniqid'

import { ensureFreshToken } from '@/helpers'

import store from '@/store'
import { useAuthStore, useSetupStore, useFileManagerStore } from '@/stores'
import { timestamp2date } from '@/filters'

export default {
    name: 'Upload',
    props: {
        accept: {
            type: String,
            default: '',
        },
        tipFiletypes: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            invalidFiles: [], // old / new
            modal: {
                visibility: false,
                all: false,
            },
        }
    },
    computed: {
        uploadHeaders() {
            return {
                authorization: `Bearer ${useAuthStore().token}`,
                accept: 'application/json',
            }
        },
        uploadData() {
            return {
                'context[type]': 'original'
            }
        },
        uploadEndpoint() {
            let path = `${this.setupStore.uploadEndpoint}/public`

            const cmsID = this.activeCms ? this.activeCms.id : null
            if (cmsID) path += `/${cmsID}`

            return path
        },
        setupStore() {
            return useSetupStore()
        },
        activeCms() {
            return this.setupStore.activeCms
        },
        uploadTipFiletypes() {
            return this.tipFiletypes || this.$t('all')
        },
        fileManagerStore() {
            return useFileManagerStore()
        }
    },
    methods: {
        customUploadRequest({ file, onProgress, onSuccess, onError }) {
            this.updateUploadHeaders().then(() => {
                const formData = new FormData()
                formData.append('file', file)
                formData.append('context[type]', 'original')

                const raw = file.raw || file

                if (raw.action === 'replace') {
                    formData.append('replace', true)
                } else if (raw.action === 'rename') {
                    formData.append('rename', true)
                }

                const xhr = new XMLHttpRequest()

                if (xhr.upload) {
                    xhr.upload.onprogress = e => {
                        if (e.total > 0) {
                            e.percent = e.loaded / e.total * 100
                        }
                        onProgress(e)
                    }
                }

                xhr.onerror = () => {
                    onError(new Error('Upload failed'))
                }

                xhr.onload = () => {
                    if (xhr.status < 200 || xhr.status >= 300) {
                        return onError(new Error(`Upload failed with status ${xhr.status}`))
                    }
                    try {
                        const response = JSON.parse(xhr.responseText)
                        onSuccess(response)
                    } catch (e) {
                        onError(e)
                    }
                }

                xhr.open('POST', this.uploadEndpoint, true)

                Object.keys(this.uploadHeaders).forEach(key => {
                    xhr.setRequestHeader(key, this.uploadHeaders[key])
                })

                xhr.send(formData)
            }).catch(error => {
                onError(error)
            })
        },
        async updateUploadHeaders() {
            // The computed uploadHeaders reads the store token; make sure it is fresh.
            await ensureFreshToken()
        },
        validateUpload(data) {
            return new Promise(async (resolve, reject) => {
                await this.updateUploadHeaders()

                if (!this.isAcceptedFile(data)) {
                    this.$notify.error({
                        title: this.$t('upload.notUploaded'),
                        message: this.$t('upload.validation', { filetypes: this.uploadTipFiletypes, size: '2GB' }),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                    return reject('invalid type')
                }

                if (data.size / 1024 / 1024 > 2000) {
                    this.$notify.error({
                        title: this.$t('upload.sizeExceeded'),
                        message: this.$t('upload.max', { size: '2GB' }),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                    return reject('too large')
                }

                // check if a file with the same name exists
                this.checkIfFileExists({ name: data.name, path: `file-manager/cms-${this.activeCms.id}` })
                    .then(file => {
                        if (file) {
                            // return reject('file exists already')
                            data.resolve = resolve
                            data.reject = reject

                            // push invalid file to invalidFiles arra
                            this.invalidFiles.push({
                                new: data,
                                old: file,
                            })

                            // open the modal (if not open already)
                            // to ask how to handle
                            // --> replace | keep both | skip
                            this.modal.visibility = true

                            // file.replace = true
                        } else {
                            return resolve()
                        }
                    })
                    .catch(err => {
                        return reject(err)
                })
            })
        },
        isAcceptedFile(file) {
            if (!this.accept) return true

            const mime = (file.type || '').toLowerCase()
            const name = (file.name || '').toLowerCase()
            const rules = this.accept.split(',').map(rule => rule.trim().toLowerCase()).filter(Boolean)

            return rules.some(rule => {
                if (rule.endsWith('/*')) {
                    const baseType = rule.slice(0, -1)
                    return mime.startsWith(baseType)
                }

                if (rule.startsWith('.')) {
                    return name.endsWith(rule)
                }

                return mime === rule
            })
        },
        uploadSucceed(response, data) {
            let file = response
            file.upload = true
            file.updated_at = Math.round(data.raw.lastModified / 1000)
            file.cms = { id: this.activeCms.id }
            if ( typeof file.size === 'number' ) file.size = file.size.toString()
            delete file.hash
            delete file.dimensions

            const action = _.get(data.raw, 'action', 'add')
            let method = 'add'

            switch (action) {
                case 'rename':
                    // file.name = `${uniqid()}.${file.name}`
                    break;

                case 'replace':
                    method = 'replace'
                    file.original_id = data.raw.original_id
                    break;
            }

            this[method](file)
                .then(savedFile => this.$emit('change', savedFile ? { ...file, ...savedFile } : file))
                .finally(() => {
                    setTimeout(() => {
                        if (this.$refs.upload !== undefined) {
                            this.$refs.upload.uploadFiles = this.$refs.upload.uploadFiles.filter(el => el.uid !== data.uid)
                        }
                    }, 1500)
                })
        },
        async uploadFailed(err, file) {
            // await this.updateUploadHeaders()

            this.$notify.error({
                title: this.$t('upload.notUploaded'),
                message: _.get(file, 'name'),
                iconClass: 'fas el-icon-fa-xmark',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            })
        },
        formatDate(timestamp) {
            return timestamp2date(timestamp, this.$t('yyyy-MM-dd') + ', HH:mm')
        },
        skipFileUpload() {
            if (this.modal.all) {
                this.invalidFiles.forEach(el => el.new.reject())
                this.invalidFiles = []
            } else {
                this.invalidFiles[0].new.reject()
                this.invalidFiles.shift()
            }

            if (! this.invalidFiles.length) this.closeModal()
        },
        replaceFile() {
            if (this.modal.all) {
                this.invalidFiles.forEach(el => {
                    el.new.action = 'replace'
                    el.new.original_id = el.old.id
                    el.new.resolve()
                })
                this.invalidFiles = []
            } else {
                this.invalidFiles[0].new.action = 'replace'
                this.invalidFiles[0].new.original_id = this.invalidFiles[0].old.id
                this.invalidFiles[0].new.resolve()
                this.invalidFiles.shift()
            }

            if (! this.invalidFiles.length) this.closeModal()
        },
        renameFile() {
            if (this.modal.all) {
                this.invalidFiles.forEach(el => {
                    el.new.action = 'rename'
                    el.new.resolve()
                })
                this.invalidFiles = []
            } else {
                this.invalidFiles[0].new.action = 'rename'
                this.invalidFiles[0].new.resolve()
                this.invalidFiles.shift()
            }

            if (! this.invalidFiles.length) this.closeModal()
        },
        closeModal() {
            this.modal.visibility = false
            this.modal.all = false

            // this.$notify.success({
            //     title: this.$t('All files were processed successfully'),
            //     iconClass: 'fas el-icon-fa-check',
            //     position: 'bottom-left',
            //     customClass: 'dark',
            //     duration: 8000,
            // })
        },
        checkIfFileExists(payload) {
            return this.fileManagerStore.checkIfFileExists(payload)
        },
        add(file) {
            return this.fileManagerStore.addFile(file)
        },
        replace(file) {
            return this.fileManagerStore.replaceFile(file)
        }
    }
}
</script>

<style lang="sass" scoped>
    .upload
        width: 100%
    .el-form > .el-form-item:last-child
        margin-bottom: 0
    .upload
        &:deep()
            .el-dialog__body
                padding-top: 0
    .file-modal-form
        h3
            margin-top: 0
        .flex
            display: flex
            width: 100%
            .col
                flex-basis: 50%
                display: grid
                grid-template-rows: auto auto
                grid-template-columns: fit-content(20%) auto
                & > p
                    grid-row: 1
                    grid-column: 1 /3
                dd
                    text-overflow: ellipsis
                    white-space: nowrap
                    overflow: hidden
                    width: 215px
            .icon
                color: $--color-primary
                font-size: 6.7rem
                i
                    margin-left: -12px
                    &.el-icon-document-delete
                        color: $--color-warning
    .modal-footer
        display: flex
        justify-content: space-between
        .left
            display: flex
            align-items: center
            .switch
                height: 28px
                line-height: 28px
</style>
